import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { post, put } from "~/Services";
import { passengerHost } from "~/Utils";
import { fields } from "../constants";
import { useSubCategoriesDocumentDetails } from "./youtube.hooks";
import { useLocalStorage } from "~/Hooks/useLocalStorage";

const AddEditYouTube = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();
  const [currentAirport] = useLocalStorage("currentAirport", null);

  const {
    data: editSubCategories,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchSubCategories,
  } = useSubCategoriesDocumentDetails(query.get("editId") || "");

  const {
    data: editSubCategoriesById,
    isLoading: isLoadingSubCategories,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchSubCategories();
    }
  }, [query.get("editId"), query.get("id")]);

  const epcApplicationsFormConfig: FormConfig = {
    initialValues: {
      title: decodeHTML(editSubCategoriesById?.payload?.title) || decodeHTML(editSubCategories?.title) || "",
      thumnail_text_english:
        decodeHTML(editSubCategoriesById?.payload?.thumnail_text_english) ||
        decodeHTML(editSubCategories?.thumnail_text_english) ||
        "",
      thumnail_text_malay:
        decodeHTML(editSubCategoriesById?.payload?.thumnail_text_malay) ||
        decodeHTML(editSubCategories?.thumnail_text_malay) ||
        "",
      thumnail_text_mandarin:
        decodeHTML(editSubCategoriesById?.payload?.thumnail_text_mandarin) ||
        decodeHTML(editSubCategories?.thumnail_text_mandarin) ||
        "",
      duration: decodeHTML(editSubCategoriesById?.payload?.duration) || decodeHTML(editSubCategories?.duration) || "",
      video_link:
        decodeHTML(editSubCategoriesById?.payload?.video_link) || decodeHTML(editSubCategories?.video_link) || "",
      airports: (decodeHTML(editSubCategoriesById?.payload?.airports) || decodeHTML(editSubCategories?.airports) || "")
        .split(",")
        .map((a: any) => a.trim())
        .filter(Boolean),
      sequence: decodeHTML(editSubCategoriesById?.payload?.sequence) || decodeHTML(editSubCategories?.sequence),
      published: editSubCategoriesById?.payload?.published ?? editSubCategories?.published ?? false,
      redirectedURL:
        decodeHTML(editSubCategoriesById?.payload?.redirectedURL) || decodeHTML(editSubCategories?.redirectedURL) || "",
      thumbnail_img: editSubCategoriesById?.payload
        ? [
            {
              uid: "-1",
              name: "thumbnail.jpg",
              status: "done",
              url: decodeHTML(editSubCategoriesById?.payload?.thumbnail_img),
            },
          ]
        : editSubCategories
          ? [
              {
                uid: "-1",
                name: "thumbnail.jpg",
                status: "done",
                url: decodeHTML(editSubCategories?.thumbnail_img),
              },
            ]
          : "",
    },
    fields: fields.youtube,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["thumbnail_img"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "PASSENGER");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }
    if (Array.isArray(data.airports)) {
      data.airports = data.airports.join(", ");
    }

    const payload = {
      action: action,
      method: method,
      module: "Home Page",
      subModule: "youtube",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "PASSENGER_ECMS",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && { id: editSubCategories?.id || editSubCategoriesById?.payload?.id }),
        ...data,
        title: data?.thumnail_text_english,
      },
      status: status,
    };

    // console.log(payload, "tested");
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      // loading={isLoadingSubCategories}
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} youtube`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingSubCategories || drilldownLoading}
        config={epcApplicationsFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") || query.get("type") === "update" ? `${passengerHost}/api/youtube-video-crud-api-data/${editSubCategories?.id || editSubCategoriesById?.payload?.id}` : `${passengerHost}/api/create-youtube-video-crud-api-data`}`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Homepage"
      />
    </ContentWrapper>
  );
};

export default AddEditYouTube;
